import { Dayjs } from 'dayjs';
import React, { useCallback } from 'react';

import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Box, Chip, Divider, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import DatePicker from '@Components/DatePicker';
import SbTextField from '@Components/Textfield';

import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import {
  CreditDebitConfig,
  CreditDebitNoteType,
  SelectedCreditDebitParamType,
  getCreditDebitGroupKey,
} from '../utils/creditDebitNoteUtils';

export interface CreditDebitConfigSectionProps {
  noteType: CreditDebitNoteType;
  config: CreditDebitConfig;
  setConfig: React.Dispatch<React.SetStateAction<CreditDebitConfig>>;
  isWyngsMy: boolean;
  selectedAccountRows: SelectedCreditDebitParamType[];
  totalSelectedShipmentsCount: number;
  totalSelectedAmount: number;
  primaryCurrencySymbol: string;
  loadingD365: boolean;
  onSendTo365: () => void;
}

const CreditDebitConfigSection = React.memo(function CreditDebitConfigSection(props: CreditDebitConfigSectionProps) {
  const {
    noteType,
    config,
    setConfig,
    isWyngsMy,
    selectedAccountRows,
    totalSelectedShipmentsCount,
    totalSelectedAmount,
    primaryCurrencySymbol,
    loadingD365,
    onSendTo365,
  } = props;

  const theme = useTheme();
  const locale = useLocale();

  const isCredit = noteType === 'credit';
  const titleText = isCredit ? 'Credit Note' : 'Debit Note';

  const handleInvoiceDateChange = useCallback(
    (date: Dayjs | null) => {
      setConfig(prev => ({
        ...prev,
        invoiceDate: date,
        dueDate: date ? date.add(1, 'month') : null,
      }));
    },
    [setConfig],
  );

  const handleDueDateChange = useCallback(
    (date: Dayjs | null) => {
      setConfig(prev => ({ ...prev, dueDate: date }));
    },
    [setConfig],
  );

  const handleRemarksChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setConfig(prev => ({ ...prev, remarks: value }));
    },
    [setConfig],
  );

  const isSendTo365Disabled = isCredit
    ? !config.invoiceDate || selectedAccountRows.length === 0 || loadingD365
    : !(config.invoiceDate && config.dueDate) || selectedAccountRows.length === 0 || loadingD365;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: '14px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.primary.main, 0.015),
        p: { xs: 1.75, sm: 2.25 },
        pb: { xs: 2.5, sm: 3 },
        overflowY: 'auto',
      }}
    >
      {/* Configuration Header */}
      <Stack direction="row" spacing={1.25} alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TuneOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.96rem', color: 'text.primary', lineHeight: 1.2 }}>
            {titleText} Configuration
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Set note dates and optional remarks
          </Typography>
        </Box>
      </Stack>

      {/* Form Controls */}
      <Grid container spacing={1.75} sx={{ flexShrink: 0 }}>
        <Grid item xs={12} sm={isCredit ? 12 : 6}>
          <DatePicker label="Invoice Date" value={config.invoiceDate} handleChange={handleInvoiceDateChange} required />
        </Grid>

        {!isCredit && (
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Due Date"
              value={config.dueDate}
              handleChange={handleDueDateChange}
              minDate={config.invoiceDate ? config.invoiceDate.add(1, 'day') : undefined}
              disabled
              required
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <SbTextField
            label="Remark"
            name="remarks"
            multiline
            rows={3}
            value={config.remarks}
            onChange={handleRemarksChange}
            placeholder="Add any remarks or notes..."
          />
        </Grid>

        {!isWyngsMy && (
          <Grid item xs={12}>
            <SbButton
              variant="outlined"
              startIcon={<CloudUploadOutlinedIcon fontSize="small" />}
              sx={{
                borderRadius: '8px !important',
                padding: '6px 14px !important',
                fontSize: '0.78rem !important',
                width: '100%',
              }}
              onClick={onSendTo365}
              disabled={isSendTo365Disabled}
              loading={loadingD365}
            >
              Send to D365
            </SbButton>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2, flexShrink: 0 }} />

      {/* Integrated Preview Notes & Financial Summary */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '200px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DescriptionOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'text.primary' }}>
              Preview {titleText}s
            </Typography>
          </Stack>
          <Chip
            size="small"
            color={selectedAccountRows.length > 0 ? 'primary' : 'default'}
            label={`No. of ${titleText}s : ${selectedAccountRows.length}`}
            sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700 }}
          />
        </Stack>

        {/* Compact Queued Note List */}
        <Box
          sx={{
            flex: 1,
            minHeight: '100px',
            maxHeight: '210px',
            overflowY: 'auto',
            borderRadius: '10px',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            p: selectedAccountRows.length === 0 ? 2 : 1,
          }}
        >
          {selectedAccountRows.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 1.5, textAlign: 'center' }}>
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
                No invoices selected yet
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Select invoices or individual shipments on the left to queue {titleText.toLowerCase()}s.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              {selectedAccountRows.map((row, idx) => (
                <Box
                  key={getCreditDebitGroupKey(row) || idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                      #{idx + 1}
                    </Typography>
                    <Tooltip title={`Invoice Number: ${row.invoiceNumber}`} arrow placement="top">
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', maxWidth: '120px' }}
                      >
                        Inv #{row.invoiceNumber}
                      </Typography>
                    </Tooltip>
                    {row.systemName && (
                      <Tooltip title={`System: ${row.systemName}`} arrow placement="top">
                        <Typography noWrap sx={{ fontSize: '0.74rem', color: 'text.secondary', maxWidth: '140px' }}>
                          • {row.systemName}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                  <Tooltip
                    title={`${row.shipments.length} shipment(s) selected in this ${titleText.toLowerCase()}`}
                    arrow
                    placement="top"
                  >
                    <Chip
                      size="small"
                      label={`${row.shipments.length} ${row.shipments.length === 1 ? 'shipment' : 'shipments'}`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Bottom Summary Callout with requested bottom gap */}
        <Box
          sx={{
            mt: 1.5,
            mb: 2.5,
            p: 1.25,
            borderRadius: '10px',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
              SELECTED TOTAL ({totalSelectedShipmentsCount} SHIPMENTS)
            </Typography>
            <Tooltip
              title={`Total Selected Amount: ${primaryCurrencySymbol} ${formatNumber(totalSelectedAmount, locale)}`}
              arrow
              placement="top"
            >
              <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 800, color: 'primary.main' }}>
                {primaryCurrencySymbol} {formatNumber(totalSelectedAmount, locale)}
              </Typography>
            </Tooltip>
          </Box>
          <Chip
            size="small"
            label={selectedAccountRows.length > 0 ? 'Ready' : 'Awaiting Selection'}
            color={selectedAccountRows.length > 0 ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
});

export default CreditDebitConfigSection;
